import * as React from 'react'
import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { EyeVisible } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { endOfMonth } from 'date-fns'
import { Features } from 'generated/featureDefinitions'
import {
  AttendanceCustomPeriodEmployeeItemFragment,
  AttendanceCustomPeriodEmployeesAnalyticsDocument,
  AttendanceCustomPeriodEmployeesCollectionListDocument,
  AttendanceCustomPeriodEmployeesCollectionListQuery,
  AttendanceStateResolverStateEnum,
  TimeInsightsEntitiesClockInStatusStatusEnum,
} from 'generated/resources/graphql'
import { BankOfHoursBreakdownPanelDocument } from 'generated/resources/graphql'
import { ExportTimeBalanceDocument } from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import PayrollConceptMapping from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/PayrollConceptMapping'
import SendEmailReminderModal from 'modules/attendance/components/SendEmailReminderModal'
import {
  PAYROLL_PERIOD_TYPES,
  type PayrollPeriodTypes,
} from 'modules/attendance/components/TimeTracking/Layout/form'
import RequestModal from 'modules/attendance/components/compensation/payroll/request'
import type { CompensationReponse } from 'modules/attendance/components/compensation/payroll/types'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import {
  AttendanceFeaturesProvider,
  useAttendanceFeatures,
} from 'modules/attendance/lib/attendanceFeaturesContext'
import { timeRangeCategoryTypeEnum } from 'modules/attendance/lib/enums'
import {
  TimeTrackingDataProvider,
  useTimeTrackingData,
} from 'modules/attendance/lib/timeTrackingDataContext'
import { useEmployeeFilters } from 'modules/employees/lib/filters'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import CustomPeriodEmployeeSidepanel from './CustomPeriodEmployeeSidepanel'
import { downloadAnnualBalanceAction } from './DownloadAnnualBalanceAction'
import { downloadReportAction } from './DownloadReportAction'
import CustomCreateModal from './components/CustomCreateModal'
import CustomResponseModal from './components/CustomResponseModal'
import { useAlerts } from './hooks/useAlerts'
import { useBulkActionsConfig } from './hooks/useBulkActionsConfig'
import { useBulkApprovePeriod } from './hooks/useBulkApprovePeriod'
import { useBulkAttendableUpdate } from './hooks/useBulkAttendableUpdate'
import { useBulkCreatePayrollSupplement } from './hooks/useBulkCreatePayrollSupplement'
import { useColumns } from './hooks/useColumns'
import { managedByEmployeeEnum, useFilters } from './hooks/useFilters'
import { STATUS_FILTER_VALUES } from './lib/constants'
import { shouldShowPayrollConceptMappingModal } from './lib/shouldShowPayrollConceptMappingModal'
import {
  buildTimeTrackingDetailParamsFromParams,
  usePersistedTimeTrackingViewType,
} from './lib/timeTrackingNavigation'
import { transformFiltersToEmployeeFiltersInput } from './lib/transformFilters'
import { TimeTrackingCollectionEmployee } from './types'

type BulkActionType =
  | 'approve'
  | 'disapprove'
  | 'send-reminder'
  | 'compensate'
  | 'exclude'
  | 'include'
const isDateNavigationToday = ({ dateFrom, dateTo }: { dateFrom: Date; dateTo: Date }) => {
  const today = i18nDateToString(new Date(), 'backend')

  return [dateFrom, dateTo].every((date) => i18nDateToString(date, 'backend') === today)
}

const PER_PAGE_OPTIONS = [10, 25, 50, 100] as const
const TIME_TRACKING_COLLECTION_DEFAULT_PAGE_SIZE = 25

const TimeTrackingCollectionContent = () => {
  const { company, employee: currentEmployee } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const params = useParams()
  const { dateFrom, dateTo, cycle, onDateChange } = useAttendanceDateNavigator()
  const routeParams = useParams()
  const { alertsFilter, hasTimeTrackingInconsistencies } = useAlerts()
  const { columns, setShowComplementaryHours } = useColumns()
  const { featureAvailability: hasHourBalance } = useAttendanceFeatures('dev_hour_balance_ui')
  const { featureAvailability: canSeeHourBalance } =
    useAttendanceFeatures('hour_balance_visibility')
  const showHourBalance = hasHourBalance && canSeeHourBalance
  const [customPeriodEmployee, setCustomPeriodEmployee] =
    React.useState<TimeTrackingCollectionEmployee | null>(null)

  const clearSelectedItemsRef = React.useRef<(() => void) | null>(null)

  const { filters: employeeFilters } = useEmployeeFilters()
  const [storedPageSize, setStoredPageSize] = useLocalStorage<number>(
    LOCAL_STORAGE_KEYS.timeTrackingCollectionPageSize,
    TIME_TRACKING_COLLECTION_DEFAULT_PAGE_SIZE
  )
  const pageSize = storedPageSize ?? TIME_TRACKING_COLLECTION_DEFAULT_PAGE_SIZE

  const [timeTrackingState, setTimeTrackingState] = React.useState<{
    showCustomPeriodEmployeeSidepanel: boolean
    payrollCompensationModalOpen: boolean
    payrollCompensationRequestModalOpen: boolean
    payrollCompensationResponseModalOpen: boolean
    payrollConceptMappingModalOpen: boolean
    selectedEmployeeIds: string[]
    selectedEmployees: AttendanceCustomPeriodEmployeeItemFragment[]
    selectedPayrollPeriod: PayrollPeriodTypes
    response: CompensationReponse
    emailReminderModalOpen: boolean
  }>({
    showCustomPeriodEmployeeSidepanel: false,
    payrollCompensationModalOpen: false,
    payrollCompensationRequestModalOpen: false,
    payrollCompensationResponseModalOpen: false,
    payrollConceptMappingModalOpen: false,
    selectedEmployeeIds: [],
    selectedEmployees: [],
    selectedPayrollPeriod: PAYROLL_PERIOD_TYPES.current,
    response: { successes: [], errors: [] },
    emailReminderModalOpen: false,
  })

  const toggleTimeTrackingState = React.useCallback(
    <K extends keyof typeof timeTrackingState>(key: K, value: (typeof timeTrackingState)[K]) => {
      setTimeTrackingState((prev) => ({ ...prev, [key]: value }))
    },
    []
  )

  const { featureAvailability: timeBalanceEnabled } = useAttendanceFeatures(
    Features.time_balance_annualisation_export
  )

  const { featureAvailability: hasCompensations } = useAttendanceFeatures(
    Features.compensations_management
  )

  const { data: exportTimeBalanceData } = useQuery(ExportTimeBalanceDocument, {})
  const canAccessTimeBalanceExport =
    !!exportTimeBalanceData?.timeBalance?._policies.accessSeeTimeBalanceExport.can
  const showExportYearlyBalanceOption = timeBalanceEnabled && canAccessTimeBalanceExport

  const { bulkApprovePeriod, bulkDisapprovePeriod } = useBulkApprovePeriod({
    cycle,
    startOn: i18nDateToString(dateFrom, 'backend'),
    endOn: i18nDateToString(dateTo, 'backend'),
  })

  const { data: bankOfHoursData, refetch: refetchBankOfHours } = useQuery(
    BankOfHoursBreakdownPanelDocument,
    {
      variables: {
        companyId: company.id,
      },
    }
  )

  const reimbursements =
    bankOfHoursData?.compensations.reimbursablePayrollConceptsConnection.nodes ?? []
  const timeRangeCategories =
    bankOfHoursData?.timeSettings.customTimeRangeCategoriesConnection.nodes ?? []
  const payrollConcepts = bankOfHoursData?.payroll.conceptsConnection.nodes ?? []

  const currentEmployeeId = currentEmployee?.id
  const { analyticsFilters, updateFilters } = useFilters({
    currentEmployeeId,
    hasTimeTrackingInconsistencies,
  })
  const {
    selectAll,
    excludedEmployeeIds,
    filters,
    search,
    setSelectAll,
    setSelectedEmployeeIds,
    setExcludedEmployeeIds,
    setSelectedEmployeeCount,
    setTotalEmployeeCount,
  } = useTimeTrackingData()

  const trackingData = React.useMemo(
    () => ({
      period_type: cycle,
      start_on: i18nDateToString(dateFrom, 'backend'),
      end_on: i18nDateToString(dateTo, 'backend'),
    }),
    [cycle, dateFrom, dateTo]
  )
  const persistViewType = usePersistedTimeTrackingViewType()

  const openSidepanel = (item: TimeTrackingCollectionEmployee, trackingEvent: string) => {
    track(trackingEvent, trackingData)
    setCustomPeriodEmployee(item)
    toggleTimeTrackingState('showCustomPeriodEmployeeSidepanel', true)
  }

  const goToEmployeeTimesheet = (employeeId: number) => {
    track(trackingEvents.timeTracking.goToEmployeeTimeSheet, trackingData)
    const detailParams = buildTimeTrackingDetailParamsFromParams(routeParams, dateFrom, cycle)
    persistViewType(detailParams)
    navigate(
      navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath({
        ...detailParams,
        employeeId,
      })
    )
  }

  const { data: analyticsData } = useQuery(AttendanceCustomPeriodEmployeesAnalyticsDocument, {
    variables: {
      companyId: company.id,
      endOn: i18nDateToString(dateTo, 'backend'),
      startOn: i18nDateToString(dateFrom, 'backend'),
      ...analyticsFilters,
      reportingToIds: analyticsFilters.reportingTo,
      search: search && search.length > 0 ? search : undefined,
    },
  })

  React.useEffect(() => {
    const totalCount = analyticsData?.attendance?.customPeriodEmployeesConnection?.totalCount ?? 0
    setTotalEmployeeCount(totalCount)
  }, [analyticsData, setTotalEmployeeCount])

  const defaultGranularity = React.useMemo<'week' | 'month' | 'day'>(() => {
    if (cycle === 'weekly') return 'week'
    if (cycle === 'monthly') return 'month'
    return 'day'
  }, [cycle])

  const reviewStatusResult =
    analyticsData?.attendance?.customPeriodEmployeesConnection?.aggregate?.terms?.reviewStatus

  const { getBulkActions } = useBulkActionsConfig({
    isDateNavigationToday: isDateNavigationToday({ dateFrom, dateTo }),
    params,
    navigate,
    navigation,
    reviewStatusResult,
  })
  const { bulkUpdateAttendable } = useBulkAttendableUpdate()
  const { bulkCompensatePeriods } = useBulkCreatePayrollSupplement()

  const countItemsForReviewStatus = useCallback(
    (reviewStatusSelected: AttendanceStateResolverStateEnum) => {
      return () => {
        if (!reviewStatusResult) return 0

        const index = reviewStatusResult?.values.findIndex(
          (status) => status === reviewStatusSelected
        )

        return index !== undefined ? (reviewStatusResult.count?.[index] ?? 0) : 0
      }
    },
    [reviewStatusResult]
  )

  const source = useGraphqlDataCollectionSource(
    {
      document: AttendanceCustomPeriodEmployeesCollectionListDocument,
      perPage: pageSize,
      mapVariables: ({
        filters: collectionFilters,
        sortings,
        search: collectionSearch,
        navigationFilters,
      }) => {
        const dateFromActive = navigationFilters?.date?.value?.from || dateFrom
        const dateToActive = navigationFilters?.date?.value?.to || dateTo
        const today = new Date()
        const effectiveEndDate =
          dateToActive && dateToActive.getTime() > today.getTime() ? today : (dateToActive ?? today)
        let cycleGranularity: 'weekly' | 'monthly' | 'daily' = 'daily'
        if (navigationFilters?.date?.granularity === 'week') {
          cycleGranularity = 'weekly'
        } else if (navigationFilters?.date?.granularity === 'month') {
          cycleGranularity = 'monthly'
        }

        if (dateFromActive && dateToActive) {
          onDateChange([dateFromActive, dateToActive])
          window.history.pushState(
            null,
            '',
            navigation.timeTracking.list.periodTypeYearMonthDay.fullPath({
              periodType: cycleGranularity,
              year: Number(dateFromActive.getFullYear()),
              month: Number(dateFromActive.getMonth() + 1),
              day: Number(dateFromActive.getDate()),
            })
          )
        }

        const {
          onlyMyReportees,
          reportingTo,
          timeInsightsInconsistencyTypes,
          onlyDirectReports,
          timeoffManagerIds,
          onlyActive,
          teamIds,
          legalEntityIds,
          locationIds,
          reviewStatus,
          onlyNonAttendable,
          hasWorkedLessThanPlanned,
        } = updateFilters(collectionFilters, collectionSearch)

        const isViewingToday = isDateNavigationToday({
          dateFrom: dateFromActive,
          dateTo: dateToActive,
        })

        return {
          companyId: company.id,
          onlyMyReportees,
          reportingToIds: reportingTo,
          timeInsightsInconsistencyTypes,
          onlyDirectReports,
          timeoffManagerIds,
          onlyActive,
          sort: sortings?.[0],
          search: collectionSearch,
          teamIds: teamIds?.length ? teamIds : undefined,
          legalEntityIds: legalEntityIds?.length ? legalEntityIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
          reviewStatus,
          hasTimeTrackingInconsistencies,
          includeClockInStatuses: isViewingToday,
          startOn: i18nDateToString(dateFromActive, 'backend'),
          endOn: i18nDateToString(effectiveEndDate, 'backend'),
          onlyNonAttendable,
          hasWorkedLessThanPlanned,
        }
      },
      mapPagination: (data) => {
        return {
          total: data.attendance.customPeriodEmployeesConnection.totalCount ?? 0,
        }
      },
      mapData: (
        data: AttendanceCustomPeriodEmployeesCollectionListQuery
      ): TimeTrackingCollectionEmployee[] => {
        const employees = data.attendance.customPeriodEmployeesConnection.edges.map(
          (edge) => edge.node
        )
        const clockInStatuses = data.timeInsights?.clockInStatusesConnection?.nodes || []

        const clockInStatusMap = new Map<string, TimeInsightsEntitiesClockInStatusStatusEnum>()
        clockInStatuses.forEach((status) => {
          if (status.employee?.id && status.status) {
            clockInStatusMap.set(status.employee.id.toString(), status.status)
          }
        })

        const employeesWithStatus = employees.map((employee) => ({
          ...employee,
          clockInStatus: clockInStatusMap.get(employee.id?.toString() || '') ?? undefined,
          isDaily: employee.referenceContract?.frForfaitJours,
        }))

        setShowComplementaryHours(
          employeesWithStatus.some((employee) => {
            const isPartTime = employee?.referenceContract?.partTime
            const enableComplementaryHours =
              employee?.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]
                ?.node?.enableComplementaryHours
            return isPartTime && enableComplementaryHours
          })
        )

        return employeesWithStatus
      },
      itemOnClick: (item) => {
        return () => {
          if (showHourBalance) {
            openSidepanel(item, trackingEvents.timeTracking.openTTCollectionSidepanelFromRow)
            return
          }

          if (item.id) goToEmployeeTimesheet(Number(item.id))
        }
      },
      itemActions: (item) =>
        showHourBalance
          ? [
              {
                label: i18n.t('time_tracking.table_collection.employee_summary.title'),
                icon: EyeVisible,
                type: 'primary',
                onClick: () => {
                  openSidepanel(
                    item,
                    trackingEvents.timeTracking.openTTCollectionSidepanelFromDetailsButton
                  )
                },
              },
            ]
          : [],
      sortings: {
        firstName: {
          label: i18n.t('employee_form.first_name'),
        },
        lastName: {
          label: i18n.t('employee_form.last_name'),
        },
        alertsCount: {
          label: i18n.t('time_tracking.table_collection.alerts'),
        },
      },
      defaultSortings: {
        field: 'firstName',
        order: 'asc',
      },
      search: {
        enabled: true,
      },
      navigationFilters: {
        date: {
          type: 'date-navigator',
          defaultValue: dateFrom,
          defaultFrom: dateFrom,
          defaultTo: dateTo,
          defaultGranularity: defaultGranularity,
          granularity: ['week', 'month', 'day'],
          max: endOfMonth(new Date()),
        },
      },
      filters: {
        reviewStatus: {
          type: 'in',
          label: i18n.t('time_tracking.table_collection.filters.review_status'),
          options: {
            options: [
              {
                label: i18n.t('time_tracking.table_collection.filters.review_status_pending'),
                value: 'pending',
              },
              {
                label: i18n.t('time_tracking.table_collection.filters.review_status_approved'),
                value: 'approved',
              },
              {
                label: i18n.t('time_tracking.table_collection.filters.review_status_in_progress'),
                value: 'in_progress',
              },
            ],
          },
        },
        status: {
          type: 'in',
          label: i18n.t('time_tracking.table_collection.filters.status'),
          options: {
            options: [
              {
                label: i18n.t('time_tracking.table_collection.filters.excluded_employees'),
                value: STATUS_FILTER_VALUES.ONLY_NON_ATTENDABLE,
              },
              // TODO: Re-enable "Only active" filter in the future
              // {
              //   label: i18n.t('time_tracking.table_collection.filters.only_active'),
              //   value: STATUS_FILTER_VALUES.ONLY_ACTIVE,
              // },
            ],
          },
        },
        workedLess: {
          type: 'in',
          label: i18n.t('time_tracking.table_collection.filters.worked_less'),
          options: {
            options: [
              {
                label: i18n.t('time_tracking.table_collection.filters.worked_less_option'),
                value: true,
              },
            ],
          },
        },
        team: employeeFilters.team,
        workplace: employeeFilters.workplace,
        legalEntity: employeeFilters.legalEntity,
        directReports: {
          type: 'in',
          label: i18n.t('time_tracking.table_collection.filters.managed_by_me'),
          options: {
            options: [
              {
                label: i18n.t('time_tracking.table_collection.filters.managed_by_me_option'),
                value: managedByEmployeeEnum.managed_by_me,
              },
              {
                label: i18n.t('time_tracking.table_collection.filters.managed_by_others'),
                value: managedByEmployeeEnum.managed_by_others,
              },
              {
                label: i18n.t(
                  'time_tracking.table_collection.filters.managed_directly_by_me_option'
                ),
                value: managedByEmployeeEnum.managed_directly_by_me,
              },
              {
                label: i18n.t(
                  'time_tracking.table_collection.filters.managed_timeoff_by_me_option'
                ),
                value: managedByEmployeeEnum.managed_timeoff_by_me,
              },
            ],
          },
        },
        reportingTo: employeeFilters.reportingTo,
        ...alertsFilter,
      },
      presets: [
        {
          label: i18n.t('time_tracking.table_collection.filters.review_status_pending'),
          filter: {
            reviewStatus: ['pending'],
          },
          itemsCount: countItemsForReviewStatus('pending'),
        },
        {
          label: i18n.t('time_tracking.table_collection.filters.review_status_approved'),
          filter: {
            reviewStatus: ['approved'],
          },
          itemsCount: countItemsForReviewStatus('approved'),
        },
      ],
      selectable: (item) => item.id ?? undefined,
      allPagesSelection: true,
      totalItemSummary: (totalCount) =>
        i18n.tp('time_tracking.table_collection.count_employees', { count: totalCount }),
      secondaryActions: () =>
        compact([
          downloadReportAction({
            params,
            navigate,
            navigation,
          }),
          downloadAnnualBalanceAction({
            params,
            navigate,
            navigation,
            showExportYearlyBalanceOption,
          }),
        ]),
      bulkActions: (bulkActionParams) => {
        const isSelectAllActive =
          bulkActionParams.allSelected === true || bulkActionParams.allSelected === 'indeterminate'

        // Always use the total filtered count from analytics (includes search filter and other filters)
        const totalCount =
          analyticsData?.attendance?.customPeriodEmployeesConnection?.totalCount ?? 0

        return getBulkActions({
          itemsStatus: bulkActionParams.itemsStatus,
          periodType: cycle,
          selectAll: isSelectAllActive,
          totalCount,
        })
      },
    },
    [company.id, pageSize]
  )
  const perPageSelectOptions = React.useMemo(
    () =>
      PER_PAGE_OPTIONS.map((value) => ({
        label: value.toString(),
        value: value.toString(),
      })),
    []
  )
  const handlePageSizeChange = React.useCallback(
    (value: string) => {
      const parsedValue = Number(value)
      if (Number.isNaN(parsedValue)) return
      if (pageSize !== parsedValue) {
        setStoredPageSize(parsedValue)
      }
    },
    [pageSize, setStoredPageSize]
  )

  const getTimeCategoriesWithoutMappings = (
    categories = timeRangeCategories,
    reimbursementsData = reimbursements
  ) => {
    return categories.filter((category) => {
      const reimbursement = reimbursementsData.find(
        (r) =>
          r.reimbursableId === category.id &&
          r.reimbursableType === timeRangeCategoryTypeEnum.TimeSettingsCustomTimeRangeCategory
      )
      return !reimbursement?.payrollConceptId
    })
  }

  const checkForMissingConceptMappings = (
    categories = timeRangeCategories,
    reimbursementsData = reimbursements
  ) => {
    const timeCategoriesWithoutMappings = getTimeCategoriesWithoutMappings(
      categories,
      reimbursementsData
    )
    return timeCategoriesWithoutMappings.length > 0
  }

  const onCompensatePeriods = async () => {
    const payrollPeriodType = timeTrackingState.selectedPayrollPeriod
    toggleTimeTrackingState('payrollCompensationModalOpen', false)

    // Refetch to ensure we have the latest mapping data
    const { data: refetchedData } = await refetchBankOfHours()

    // Extract data from refetch result
    const refetchedReimbursements =
      refetchedData?.compensations.reimbursablePayrollConceptsConnection.nodes ?? []
    const refetchedTimeRangeCategories =
      refetchedData?.timeSettings.customTimeRangeCategoriesConnection.nodes ?? []

    // Check if concept mappings are missing using refetched data
    const hasMissingMappings = checkForMissingConceptMappings(
      refetchedTimeRangeCategories,
      refetchedReimbursements
    )

    if (shouldShowPayrollConceptMappingModal({ hasCompensations, hasMissingMappings })) {
      toggleTimeTrackingState('payrollConceptMappingModalOpen', true)
      return
    }

    // Proceed with compensation if all mappings exist
    await proceedWithCompensation(payrollPeriodType)
  }

  const proceedWithCompensation = async (payrollPeriodType: PayrollPeriodTypes) => {
    toggleTimeTrackingState('payrollConceptMappingModalOpen', false)
    toggleTimeTrackingState('payrollCompensationRequestModalOpen', true)

    // Calculate transformed filters only if selectAll is true
    const filtersToSend =
      selectAll && filters ? transformFiltersToEmployeeFiltersInput(filters, search) : null

    try {
      const result = await bulkCompensatePeriods({
        employeeIds: timeTrackingState.selectedEmployeeIds.map((id) => parseInt(id, 10)),
        startOn: dateFrom,
        endOn: dateTo,
        period: payrollPeriodType,
        selectAll: selectAll,
        excludedEmployeeIds: excludedEmployeeIds,
        filters: filtersToSend,
      })

      if (result?.manualCompensation) {
        toggleTimeTrackingState('response', {
          successes: (result.manualCompensation.successes || []).map((item) => ({
            employee_id: item.employeeId || 0,
            employee_name: item.employeeName || null,
            employee_avatar_url: item.employeeAvatarUrl || null,
            error: item.error || '',
          })),
          errors: (result.manualCompensation.errors || []).map((item) => ({
            employee_id: item.employeeId || 0,
            employee_name: item.employeeName || null,
            employee_avatar_url: item.employeeAvatarUrl || null,
            error: item.error || '',
          })),
        })
      } else {
        toggleTimeTrackingState('response', { successes: [], errors: [] })
      }

      toggleTimeTrackingState('payrollCompensationRequestModalOpen', false)
      toggleTimeTrackingState('payrollCompensationResponseModalOpen', true)
      clearSelectedItemsRef.current?.()
    } catch (error) {
      toggleTimeTrackingState('response', { successes: [], errors: [] })
      toggleTimeTrackingState('payrollCompensationRequestModalOpen', false)
      toggleTimeTrackingState('payrollCompensationResponseModalOpen', true)
      clearSelectedItemsRef.current?.()
    }
  }

  const handleConceptMappingSubmit = async () => {
    // Refetch the query to get the latest mappings
    const { data: refetchedData } = await refetchBankOfHours()

    // Extract data from refetch result
    const refetchedReimbursements =
      refetchedData?.compensations.reimbursablePayrollConceptsConnection.nodes ?? []
    const refetchedTimeRangeCategories =
      refetchedData?.timeSettings.customTimeRangeCategoriesConnection.nodes ?? []

    // After mapping, check again and proceed if all mappings are complete using refetched data
    const hasMissingMappings = checkForMissingConceptMappings(
      refetchedTimeRangeCategories,
      refetchedReimbursements
    )

    if (!hasMissingMappings) {
      await proceedWithCompensation(timeTrackingState.selectedPayrollPeriod)
    }
    // If still missing mappings, the PayrollConceptMapping component will keep the submit button disabled
  }

  const onCloseCompensationModal = () => {
    toggleTimeTrackingState('payrollCompensationResponseModalOpen', false)
    toggleTimeTrackingState('selectedEmployeeIds', [])
    toggleTimeTrackingState('selectedEmployees', [])
  }

  const handleBulkAction = async (
    action: BulkActionType | string,
    selectedItems: {
      itemsStatus: readonly { checked: boolean; item: AttendanceCustomPeriodEmployeeItemFragment }[]
      allSelected?: boolean | 'indeterminate'
    }
  ) => {
    const selectedEmployeeItems = selectedItems.itemsStatus
      .filter(
        (selectedItem: { checked: boolean; item: AttendanceCustomPeriodEmployeeItemFragment }) =>
          selectedItem.checked === true
      )
      .map(
        (selectedItem: { item: AttendanceCustomPeriodEmployeeItemFragment }) => selectedItem.item
      )

    const employeeIds = selectedEmployeeItems
      .map((item) => item.id?.toString())
      .filter((id): id is string => id !== undefined)

    // Update selected employees state for compensation modal
    toggleTimeTrackingState('selectedEmployees', selectedEmployeeItems)

    const isSelectAllActive =
      selectedItems.allSelected === true || selectedItems.allSelected === 'indeterminate'

    // Calculate excludedEmployeeIds directly from selectedItems
    const excludedIdsFromSelectedItems =
      selectedItems.allSelected === 'indeterminate'
        ? selectedItems.itemsStatus
            ?.filter((item) => !item.checked)
            .map((item) => Number(item.item.id)) || []
        : []

    // Calculate transformed filters only if selectAll is true
    const filtersToSend =
      isSelectAllActive && filters ? transformFiltersToEmployeeFiltersInput(filters, search) : null

    switch (action) {
      case 'approve':
        await bulkApprovePeriod({
          employeeIds,
          selectAll: isSelectAllActive,
          excludedEmployeeIds: excludedIdsFromSelectedItems,
          filters: filtersToSend,
        })
        break
      case 'disapprove':
        await bulkDisapprovePeriod({
          employeeIds,
          selectAll: isSelectAllActive,
          excludedEmployeeIds: excludedIdsFromSelectedItems,
          filters: filtersToSend,
        })
        break
      case 'send-reminder':
        toggleTimeTrackingState('selectedEmployeeIds', employeeIds)
        toggleTimeTrackingState('emailReminderModalOpen', true)
        break
      case 'compensate':
        toggleTimeTrackingState('selectedEmployeeIds', employeeIds)
        toggleTimeTrackingState('payrollCompensationModalOpen', true)
        break
      case 'exclude':
        await bulkUpdateAttendable({
          employeeIds,
          attendable: false,
          startOn: dateFrom,
          endOn: dateTo,
          selectAll: isSelectAllActive,
          excludedEmployeeIds: excludedIdsFromSelectedItems,
          filters: filtersToSend,
        })
        break
      case 'include':
        await bulkUpdateAttendable({
          employeeIds,
          attendable: true,
          startOn: dateFrom,
          endOn: dateTo,
          selectAll: isSelectAllActive,
          excludedEmployeeIds: excludedIdsFromSelectedItems,
          filters: filtersToSend,
        })
        break
      case 'download-report':
        navigate(
          navigation.timeTracking.list.periodTypeYearMonthDay.exports.fullPath({
            periodType: params.periodType ?? '',
            year: Number(params.year),
            month: Number(params.month),
            day: Number(params.day),
          })
        )
        break
      default:
        break
    }
  }

  return (
    <>
      {showHourBalance && timeTrackingState.showCustomPeriodEmployeeSidepanel && (
        <AttendanceFeaturesProvider>
          <CustomPeriodEmployeeSidepanel
            employee={customPeriodEmployee}
            onClose={() => {
              toggleTimeTrackingState('showCustomPeriodEmployeeSidepanel', false)
              setCustomPeriodEmployee(null)
            }}
            onEmployeeUpdate={(updatedEmployee) => {
              setCustomPeriodEmployee(updatedEmployee)
            }}
          />
        </AttendanceFeaturesProvider>
      )}
      <ResponsivePage.Body>
        <OneDataCollection
          id="time-tracking-collection/v1"
          storage={{
            features: ['filters', 'search', 'sortings'],
          }}
          source={source}
          onBulkAction={handleBulkAction}
          onSelectItems={(selectedItems, clearSelectedItems) => {
            const allSelected =
              selectedItems.allSelected === true || selectedItems.allSelected === 'indeterminate'
            clearSelectedItemsRef.current = clearSelectedItems

            // - allSelected === true: all items selected (no excluded)
            // - allSelected === "indeterminate": Select All was used but some items were deselected (excluded)
            // - allSelected === false: individual selection (no Select All)
            const selectedCount = selectedItems.selectedCount || 0

            const excludedIds =
              selectedItems.allSelected === 'indeterminate'
                ? selectedItems.itemsStatus
                    ?.filter((item) => !item.checked)
                    .map((item) => Number(item.item.id)) || []
                : []

            const selectedIds =
              selectedItems.allSelected === false
                ? selectedItems.selectedIds?.map((id) => Number(id)) || []
                : []

            setSelectAll(allSelected)
            setSelectedEmployeeIds(selectedIds)
            setExcludedEmployeeIds(excludedIds)
            setSelectedEmployeeCount(selectedCount)
          }}
          visualizations={[
            {
              type: 'table',
              options: {
                columns,
              },
            },
          ]}
          emptyStates={{
            'no-results': {
              title: i18n.t('time_tracking.table_collection.empty_state.title'),
              description: i18n.t('time_tracking.table_collection.empty_state.description'),
              emoji: '🧐',
            },
          }}
        />
        <Box width="full" flexDirection="row" justifyContent="flexEnd" paddingTop="s2">
          <F0Select
            hideLabel
            size="sm"
            options={perPageSelectOptions}
            value={pageSize.toString()}
            onChange={handlePageSizeChange}
            disabled={source.isLoading}
          />
        </Box>
      </ResponsivePage.Body>
      <Outlet />
      {timeTrackingState.emailReminderModalOpen && (
        <SendEmailReminderModal
          employeeId={0}
          employeeFullName=""
          selectedEmployeeIds={timeTrackingState.selectedEmployeeIds.map((id) => Number(id))}
          onClose={() => {
            toggleTimeTrackingState('emailReminderModalOpen', false)
            toggleTimeTrackingState('selectedEmployeeIds', [])
          }}
        />
      )}
      {timeTrackingState.payrollCompensationModalOpen && (
        <CustomCreateModal
          onSubmit={onCompensatePeriods}
          onCloseModal={() => toggleTimeTrackingState('payrollCompensationModalOpen', false)}
          selected={timeTrackingState.selectedEmployees}
          selectedPayrollPeriod={timeTrackingState.selectedPayrollPeriod}
          onPayrollPeriodChange={(period: 'current' | 'next') =>
            toggleTimeTrackingState('selectedPayrollPeriod', period)
          }
        />
      )}
      {timeTrackingState.payrollConceptMappingModalOpen && bankOfHoursData && (
        <PayrollConceptMapping
          reimbursements={reimbursements}
          payrollConcepts={payrollConcepts}
          timeRangeCategories={getTimeCategoriesWithoutMappings()}
          onCancel={() => {
            toggleTimeTrackingState('payrollConceptMappingModalOpen', false)
            toggleTimeTrackingState('payrollCompensationModalOpen', true)
          }}
          onSubmit={handleConceptMappingSubmit}
        />
      )}
      {timeTrackingState.payrollCompensationRequestModalOpen && <RequestModal />}
      {timeTrackingState.payrollCompensationResponseModalOpen && (
        <CustomResponseModal
          response={timeTrackingState.response}
          onCloseModal={onCloseCompensationModal}
        />
      )}
    </>
  )
}

const TimeTrackingCollection = () => {
  return (
    <TimeTrackingDataProvider>
      <TimeTrackingCollectionContent />
    </TimeTrackingDataProvider>
  )
}

export default TimeTrackingCollection
